import PropTypes from 'prop-types'
import { createContext, useContext, useMemo } from 'react'
import { useAdminOnboardingSkip } from '../features/discover/admin-onboarding-skip'
import { mutation } from '../graphql'
import { getAuthorizationRole } from '../helpers/authorization-roles'
import {
  extractCustomerDashboardMeta,
  normalizeMeta
} from '../helpers/employee'

const UserContext = createContext()

UserContext.displayName = 'UserContext'

export function UserContextProvider({
  cognitoUser,
  activeSpace,
  refetchActiveSpace,
  isAdmin,

  employees,
  refetchEmployees,
  user,
  children
}) {
  const { skipped: adminSkippedOnboarding } = useAdminOnboardingSkip({
    isAdmin
  })

  const value = useMemo(() => {
    // normalizeMeta statt JSON.parse: fängt doppelt stringifizierte Altdaten
    // ab, die sonst hier und in den Writern (.filter) die App blockieren.
    const customer_dashboard_meta = normalizeMeta(
      activeSpace.customer_dashboard_meta
    )

    const {
      has_seen_product_tour,
      has_seen_favored_space_hint,
      has_seen_store_partner_logo_hint,
      hasSeenOrSubmittedMatchingProfileFeedback,
      hasSeenOrSubmittedTalentProfileFeedback,
      hasSeenOrSubmittedRequirementProfileFeedback,
      hasSeenHelpSupportHint,
      ui_settings_general,
      ui_space_state_hint,
      hasCompletedOnboarding: hasCompletedOnboardingInMeta
    } = extractCustomerDashboardMeta({ customer_dashboard_meta })

    // Ein Aivy-Admin kann das Onboarding in diesem Browser überspringen
    // (features/discover/admin-onboarding-skip.js). Alle Gates lesen
    // hasCompletedOnboarding, deshalb wird der Skip nur hier eingerechnet.
    // Gespeichert wird dabei nichts.
    const onboardingSkippedByAdmin =
      isAdmin && adminSkippedOnboarding && !hasCompletedOnboardingInMeta
    const hasCompletedOnboarding =
      hasCompletedOnboardingInMeta || onboardingSkippedByAdmin

    const updateSpace = {
      mutate: async ({ input }, callbacks = {}) => {
        const { onSuccess } = callbacks

        const response = await mutation({ mutation: 'updateSpace', input })
        await refetchActiveSpace()

        onSuccess && onSuccess(response)
      }
    }

    const updateUiSettingsGeneral = ({ key, value }) => {
      ui_settings_general[key] = value

      const cdm = JSON.stringify([
        ...customer_dashboard_meta.filter(({ id }) => id !== 'UI_GENERAL'),
        ui_settings_general
      ])

      updateSpace.mutate({
        input: {
          id: activeSpace.id,
          customer_dashboard_meta: cdm
        }
      })
    }

    const updateUiSettingsSpaceStateHint = ({ key, value }) => {
      ui_space_state_hint[key] = value

      const cdm = JSON.stringify([
        ...customer_dashboard_meta.filter(
          ({ id }) => id !== 'UI_SPACE_STATE_HINT'
        ),
        ui_space_state_hint
      ])

      updateSpace.mutate({
        input: {
          id: activeSpace.id,
          customer_dashboard_meta: cdm
        }
      })
    }

    return {
      cognitoUser,
      activeSpace: {
        ...activeSpace,
        customer_dashboard_meta,
        has_seen_product_tour,
        has_seen_favored_space_hint,
        has_seen_store_partner_logo_hint,
        hasSeenOrSubmittedMatchingProfileFeedback,
        hasSeenOrSubmittedTalentProfileFeedback,
        hasSeenOrSubmittedRequirementProfileFeedback,
        hasSeenHelpSupportHint,
        hasCompletedOnboarding,
        onboardingSkippedByAdmin,
        ui_settings_general,
        ui_space_state_hint,
        updateUiSettingsGeneral,
        updateUiSettingsSpaceStateHint,
        authorization_role: getAuthorizationRole({ user: cognitoUser })
      },
      refetchActiveSpace,
      isAdmin, // used for aivy employees
      employees,
      refetchEmployees,
      user
    }
  }, [
    cognitoUser,
    activeSpace,
    refetchActiveSpace,
    isAdmin,
    adminSkippedOnboarding,
    employees,
    refetchEmployees,
    user
  ])

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

UserContextProvider.propTypes = {
  cognitoUser: PropTypes.object.isRequired,
  activeSpace: PropTypes.object.isRequired,
  refetchActiveSpace: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  employees: PropTypes.array.isRequired,
  refetchEmployees: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired
}

export function useUserContext() {
  const context = useContext(UserContext)

  if (!context) {
    throw new Error('useUserContext must be used within a AppProvider')
  }

  return context
}

/**
 * Wie useUserContext, wirft aber nicht ohne Provider und gibt dann null
 * zurück. Für Hooks, die auch in Tests oder Flows ohne DataContext laufen.
 */
export function useOptionalUserContext() {
  return useContext(UserContext) ?? null
}
